import { useRecoilCallback } from 'recoil'
import {
  growthCeiling,
  type BleedMargins,
  type BleedPadding,
  type GrowthCeilingBox,
  type GrowthCeilingItem,
  type GrowthCeilingResult,
} from '@messenger/utils'
import type { Content } from '@messenger/types'
import { selectedPageAtom } from '@/atoms/editorAtoms'
import {
  baseLayoutMarginsSelector,
  editorPagesAtom,
  itemAtomFamily,
  itemIdAtom,
} from '@/atoms/_editorAtoms'

/**
 * The page a read-only surface paints, as `growthCeiling` reads it to
 * record the room under each box. The canvas has the editor's atoms and
 * needs none of this; the preview and the PDF render outside them, so
 * `ProductPrint` hands the page down.
 */
export type EditorTextBoxPageGeometry = {
  /**
   * The page's items with every Lotus box's frame merged in
   * (`hydrateEditorTextBoxItems`) — a stored Lotus item is `{ id, type }`
   * alone, and `growthCeiling` skips an item it cannot frame, so an
   * unhydrated array would let a Lotus box bound no room.
   */
  items: readonly GrowthCeilingItem[]
  margins: BleedMargins | null
  pageWidth: number
  pageHeight: number
  padding: BleedPadding | null
  /**
   * The PDF paints no hidden item, so none of them bounds a room there. The
   * editor and the preview paint them and pass them through.
   */
  dropsHiddenInPdf?: boolean
}

type ItemLike = GrowthCeilingItem & { hideInPdf?: boolean | null }

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * The box's own frame as the array that holds the blockers states it. A
 * surface that measured the box from one source and its neighbours from
 * another read a room a pixel off the other surfaces' — the editor said
 * 121 where the preview said 120.00 on the same page — and the record is
 * only worth keeping if every surface writes the same number. So the frame
 * the room is measured from comes out of `items` whenever `items` states
 * it, and the caller's frame is only the fallback for a box the array does
 * not carry.
 */
const boxFromItems = (
  box: GrowthCeilingBox,
  items: readonly GrowthCeilingItem[],
): GrowthCeilingBox => {
  const own = items.find((item) => item.id === box.id)
  if (!own) return box
  const x = asNumber(own.x)
  const y = asNumber(own.y)
  const width = asNumber(own.width)
  if (x === null || y === null || width === null) return box
  return { ...box, x, y, width }
}

/**
 * The room, and the frame it was measured from. The frame is reported
 * because two surfaces reading the same page disagreed by a pixel and the
 * numbers alone could not say which source each had used; it is stamped
 * beside the room so a capture names the difference instead of implying
 * one.
 */
export type EditorGrowthCeiling = GrowthCeilingResult & {
  measuredFrom: { x: number; y: number; width: number }
}

const ceilingOf = (
  box: GrowthCeilingBox,
  page: EditorTextBoxPageGeometry,
): EditorGrowthCeiling | null => {
  const items = page.dropsHiddenInPdf
    ? page.items.filter((item) => (item as ItemLike).hideInPdf !== true)
    : page.items
  const measured = boxFromItems(box, items)
  const result = growthCeiling({
    box: measured,
    items,
    margins: page.margins,
    pageWidth: page.pageWidth,
    pageHeight: page.pageHeight,
    padding: page.padding,
  })
  // A room that is not a usable number is no room: nothing is recorded.
  if (asNumber(result.maxHeight) === null) return null
  return {
    ...result,
    measuredFrom: { x: measured.x, y: measured.y, width: measured.width },
  }
}

/**
 * The room the page leaves under this box and what bounds it — `maxHeight`
 * in the box's own pixels and `boundedBy` naming the item, the margin, the
 * fold, or `preexisting` for a stored frame already past that room (with
 * the room, what bounds it and one line of the text in `kept`) — or `null`
 * when the surface cannot say what lies under the box. A measurement for
 * the flag, the log and the job record: no surface applies it to a frame.
 * The frame a surface paints is the frame the row holds, and the text is
 * fitted into it (`docs/lotus/sizing.md`, "Load"). `boundedBy` is what the
 * surfaces stamp as `data-text-bounded-by` and the worker asserts on, so
 * the result is handed back whole rather than reduced here. A caller that
 * hands in `box.lineHeight` gets the line recorded beside the room.
 *
 * One function, for text boxes of both kinds, so that the editor, the
 * preview and the PDF read the same page and record the same room. Off the
 * canvas the caller hands the page in; on the canvas it is read out of a
 * Recoil snapshot rather than subscribed to, because the room is wanted
 * once per load and a subscription to every item atom would recompute it
 * through every drag.
 */
export const useEditorTextBoxGrowthCeiling = (
  page: EditorTextBoxPageGeometry | null,
): ((box: GrowthCeilingBox) => EditorGrowthCeiling | null) =>
  useRecoilCallback(
    ({ snapshot }) =>
      (box: GrowthCeilingBox): EditorGrowthCeiling | null => {
        if (page) return ceilingOf(box, page)
        const read = <T>(node: Parameters<typeof snapshot.getLoadable>[0]) =>
          snapshot.getLoadable(node).valueMaybe() as T | undefined
        const ids = read<string[]>(itemIdAtom)
        if (!ids) return null
        const pages = read<Content[]>(editorPagesAtom)
        const selected = read<number>(selectedPageAtom) ?? 1
        const product = pages?.[selected - 1]?.product
        if (!product) return null
        return ceilingOf(box, {
          items: ids.flatMap((id) => {
            const item = read<GrowthCeilingItem>(itemAtomFamily(id))
            return item ? [item] : []
          }),
          margins: read<BleedMargins>(baseLayoutMarginsSelector) ?? null,
          pageWidth: product.productSize?.width ?? 0,
          pageHeight: product.productSize?.height ?? 0,
          padding: product.padding ?? null,
        })
      },
    [page],
  )
